import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar, Clock, FileText } from 'lucide-react';
import { VCardData } from '@/lib/vcard-utils';
import { cn } from '@/lib/utils';

interface VCardAdditionalInfoProps {
  data: VCardData;
  onChange: (data: Partial<VCardData>) => void;
}

// Common time zones
const timeZones = [
  { value: '', label: 'Not specified' },
  { value: 'America/New_York', label: '(UTC-5) Eastern Time' },
  { value: 'America/Chicago', label: '(UTC-6) Central Time' },
  { value: 'America/Denver', label: '(UTC-7) Mountain Time' },
  { value: 'America/Los_Angeles', label: '(UTC-8) Pacific Time' },
  { value: 'Europe/London', label: '(UTC+0) London' },
  { value: 'Europe/Paris', label: '(UTC+1) Paris/Berlin' },
  { value: 'Europe/Moscow', label: '(UTC+3) Moscow' },
  { value: 'Asia/Dubai', label: '(UTC+4) Dubai' },
  { value: 'Asia/Kolkata', label: '(UTC+5:30) India' },
  { value: 'Asia/Singapore', label: '(UTC+8) Singapore' },
  { value: 'Asia/Tokyo', label: '(UTC+9) Tokyo' },
  { value: 'Australia/Sydney', label: '(UTC+10) Sydney' },
  { value: 'Pacific/Auckland', label: '(UTC+12) Auckland' },
];

export function VCardAdditionalInfo({ data, onChange }: VCardAdditionalInfoProps) {
  const notesLength = data.notes?.length || 0;
  const notesNearLimit = notesLength > 400;
  const notesAtLimit = notesLength >= 500;

  return (
    <div className="space-y-6">
      {/* Birthday */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Birthday
        </Label>
        <Input
          type="date"
          value={data.birthday}
          onChange={(e) => onChange({ birthday: e.target.value })}
        />
      </div>

      {/* Anniversary */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Anniversary
        </Label>
        <Input
          type="date"
          value={data.anniversary}
          onChange={(e) => onChange({ anniversary: e.target.value })}
        />
        <p className="text-xs text-muted-foreground">Note: Anniversary is only supported in vCard 4.0</p>
      </div>

      {/* Time Zone */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Time Zone
        </Label>
        <Select
          value={data.timeZone}
          onValueChange={(value) => onChange({ timeZone: value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select time zone" />
          </SelectTrigger>
          <SelectContent>
            {timeZones.map((tz) => (
              <SelectItem key={tz.value || 'none'} value={tz.value || 'none'}>
                {tz.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Organization URL */}
      <div className="space-y-2">
        <Label>Organization Website</Label>
        <Input
          type="url"
          value={data.organizationUrl}
          onChange={(e) => onChange({ organizationUrl: e.target.value })}
          placeholder="https://company.com"
        />
      </div>

      {/* Notes/Bio */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Notes/Bio
          </Label>
          <span
            className={cn(
              'text-xs',
              notesAtLimit && 'text-destructive font-medium',
              notesNearLimit && !notesAtLimit && 'text-secondary-foreground',
              !notesNearLimit && 'text-muted-foreground'
            )}
          >
            {notesLength}/500
          </span>
        </div>
        <Textarea
          value={data.notes}
          onChange={(e) => {
            if (e.target.value.length <= 500) {
              onChange({ notes: e.target.value });
            }
          }}
          placeholder="A brief bio or notes about yourself..."
          className="min-h-[100px] resize-none"
          maxLength={500}
        />
      </div>
    </div>
  );
}
